import styles from './CertificateModal.module.scss';
import { Text } from '../../typography';
import Modal from '../Modal';
import { useModal } from '../../../providers/ModalProvider';
import { useTranslation } from 'react-i18next';
import { modals } from '../../../providers/modals';
import { keys } from '../../../utilities/translator/translation_keys';
import { loader } from 'graphql.macro';
import { useMutation } from '@apollo/client';
import { useEffect, useState, useMemo } from 'react';
import Button from '../../Button';
import { Form } from 'react-bootstrap';
import { PDFDownloadLink } from '@react-pdf/renderer';
import CertificatePDF from '../../../utilities/pdf_export/certificate';
import { useCurrentUser } from '../../../providers/UserProvider';
import constants from '../../../styles/contants.module.scss';
import TextWithIcon from '../../typography/TextWithIcon';
import UserSelector from '../../UserSelector';

const updateCertificateMutation = loader(
  './CertificateModal.updateCertificate.graphql',
);
const createCertificateMutation = loader(
  './CertificateModal.createCertifcate.graphql',
);

const deleteCertificateMutation = loader(
  './CertificateModal.deleteCertificate.graphql',
);

export default function CertificateModal() {
  const { modalState, closeModal, openConfirmationModal } = useModal();
  const { company } = useCurrentUser();
  const { t } = useTranslation();

  const {
    show,
    certificate,
    requirement,
    user,
    onComplete = () => {},
  } = modalState.certificateModal;

  const onHide = () => {
    onComplete();
    setState({});
    closeModal({ modalName: modals.certificate });
  };
  const [state, setState] = useState(certificate);

  useEffect(() => {
    if (!!certificate) {
      setState(certificate);
    }
  }, [certificate]);
  const [updateCertificate] = useMutation(updateCertificateMutation);
  const [deleteCertificate] = useMutation(deleteCertificateMutation);
  const [createCertificate] = useMutation(createCertificateMutation);
  const handleSave = async () => {
    try {
      const baseVars = {
        ...state,
        requirementId: requirement.id,
        userId: state?.user?.id || user?.id,
      };
      state?.id
        ? await updateCertificate({
            variables: {
              id: state?.id,
              ...baseVars,
            },
          })
        : await createCertificate({ variables: { ...baseVars } });
    } catch (err) {
      console.error('Save certificate failed', err);
    } finally {
      onHide();
      onComplete();
    }
  };

  const saveDisabled = useMemo(() => !state?.user || !state?.title, [state]);

  return (
    <Modal
      disableClickOutside
      open={show || false}
      onClose={onHide}
      variant={'primary'}
      title={
        !user?.id
          ? `Create ${requirement?.title} Certificate`
          : `${user?.firstName} ${user?.lastName}'s ${requirement?.title}`
      }
      hideCancel
      titleImage={
        <img
          className={styles.taskImage}
          src={'https://app.opasmobile.com/assets/graphics/cert_1247.png'}
          alt="task_png"
        />
      }
      hideSubmit
      disableCloseOnSubmit={true}
      className={styles.modalMinWidth}
    >
      <div className={styles.container}>
        <div className={styles.flex}>
          <Text noMargin weight="semiBold" size="md">
            {t(keys.common.USER)}
            <span className={constants.red}>*</span>
          </Text>
          <UserSelector
            type="all"
            cardProps={{ largeAvatar: true }}
            selected={state?.user}
            onSelectionChanged={(user) => setState({ ...state, user })}
            onClose={() => setState({ ...state, user: null })}
          />
        </div>
        <div className={styles.flex}>
          <Text noMargin weight="semiBold" size="md">
            {t(keys.common.TITLE)}
            <span className={constants.red}>*</span>
          </Text>
          <Form.Control
            placeholder={`Enter title`}
            value={state?.title}
            onChange={(e) => setState({ ...state, title: e.target.value })}
          />
        </div>
        <div className={styles.flex}>
          <Text noMargin weight="semiBold" size="md">
            {t(keys.common.DESCRIPTION)}
          </Text>
          <Form.Control
            as="textarea"
            placeholder={`Enter Description`}
            rows={2}
            value={state?.description || ''}
            onChange={(e) =>
              setState({ ...state, description: e.target.value })
            }
          />
        </div>
        <div className={styles.flex}>
          <Text noMargin weight="semiBold" size="md">
            Training Provider
          </Text>
          <Form.Control
            placeholder="Enter Training Provider"
            value={state?.provider}
            onChange={(e) => setState({ ...state, provider: e.target.value })}
          />
        </div>
        <div className={styles.flex}>
          <Text className={styles.label} noMargin weight="semiBold">
            Date Issued
          </Text>
          <input
            type="date"
            className={styles.dateSelector}
            value={state?.dateIssued}
            onChange={(e) => {
              const newDate = e.target.value;
              setState({ ...state, dateIssued: newDate ?? null });
            }}
          />
        </div>
        <div className={styles.flex}>
          <Text className={styles.label} noMargin weight="semiBold">
            {t(keys.action.EXPIRES)}
          </Text>
          <input
            type="date"
            className={styles.dateSelector}
            value={state?.expires}
            onChange={(e) => {
              const newDate = e.target.value;
              setState({ ...state, expires: newDate });
            }}
          />
        </div>
        {!!certificate?.id ? (
          <PDFDownloadLink
            document={
              <CertificatePDF
                logo={company?.logoUrl}
                certificate={certificate}
              />
            }
          >
            <TextWithIcon
              leftIcon="download"
              weight="semiBold"
              textAlign="right"
              color="accentPrimary"
            >
              Download PDF
            </TextWithIcon>
          </PDFDownloadLink>
        ) : null}
        <br />
        <div className={styles.flexRight}>
          {certificate?.id && (
            <Button
              value="Delete"
              icon="delete"
              variant="danger"
              onClick={() => {
                openConfirmationModal({
                  title: 'Delete Certificate',
                  description: `Are you sure you want to delete this Certificate? This action cannot be undone.`,
                  buttonText: 'Delete',
                  variant: 'danger',
                  onSubmit: () => {
                    deleteCertificate({
                      variables: { id: certificate?.id },
                    }).then(() => {
                      onHide();
                    });
                  },
                });
              }}
            />
          )}
          <Button
            disabled={saveDisabled}
            value="Save"
            icon="check_circle"
            onClick={handleSave}
          />
        </div>
      </div>
    </Modal>
  );
}
